import { z } from 'zod';

/**
 * One player's effective ownership within a specific mini-league's rival
 * pool — not the global field `selectedByPercent` reads from, and not the
 * curated elite panel `eliteOwnedBy` reads from either. `ownedBy` and
 * `captainedBy` are plain rival counts; `effectiveOwnershipPercent` is the
 * weighted figure computeEffectiveOwnership derives from them (each held
 * pick counts once, a captained pick counts once more per extra multiplier
 * beyond 1 — so a triple captain counts three times).
 */
export const EffectiveOwnershipEntry = z.object({
  fplId: z.number().int(),
  /** How many of the rivals read hold this player, bench included. */
  ownedBy: z.number().int().min(0),
  /** How many of the rivals read captained this player this gameweek. */
  captainedBy: z.number().int().min(0),
  effectiveOwnershipPercent: z.number().min(0),
});
export type EffectiveOwnershipEntry = z.infer<typeof EffectiveOwnershipEntry>;

/**
 * Effective ownership across a mini-league's top-ranked rivals, keyed by
 * FPL element id. `rivalsRead` is the honest denominator every percentage
 * above is computed against — how many rivals' picks were actually
 * fetched, not the league's full size — and `rivalCap` is the top-N limit
 * that was applied to the league's standings before fetching, so a caller
 * can tell "the top N" from "the league" at a glance.
 */
export const EffectiveOwnership = z.object({
  byFplId: z.record(z.string(), EffectiveOwnershipEntry),
  rivalsRead: z.number().int().min(0),
  rivalCap: z.number().int().positive(),
});
export type EffectiveOwnership = z.infer<typeof EffectiveOwnership>;
